/**
 * Global Store
 * 管理应用级别的全局状态
 * - 标签页状态（多标签页控制）
 * - 主题设置
 * - 语言设置
 * - 设备信息
 * - 等等
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useDocumentVisibility } from '@vueuse/core'
import { getDeviceInfo } from '@/utils'
import { getStaticConfig } from '@/api'

const SUPPORT_LOCALES = ['zh-CN', 'en-US', 'pt-BR']

/**
 * 获取浏览器语言
 */
function getBrowserLocale() {
  const browserLocale = navigator.language || navigator.userLanguage

  // 精确匹配
  if (SUPPORT_LOCALES.includes(browserLocale)) {
    return browserLocale
  }

  // 模糊匹配（例如 zh-TW 匹配到 zh-CN, pt-PT 匹配到 pt-BR）
  const languageCode = browserLocale.split('-')[0]
  const matchedLocale = SUPPORT_LOCALES.find(locale =>
    locale.split('-')[0] === languageCode
  )

  return matchedLocale || 'en-US' // 默认英文
}

export const useGlobalStore = defineStore('global', () => {
  // ========== 标签页活跃状态 ==========
  /**
   * 标识当前标签页是否是活跃标签页
   * true: 当前标签页持有应用锁，可以执行登录、初始化等操作
   * false: 当前标签页未持有锁，是非活跃标签页，不能执行登录等操作
   */
  const isActiveTab = ref(false)

  /**
   * 设置标签页活跃状态
   * @param {boolean} value - true 表示活跃标签页，false 表示非活跃标签页
   */
  function setActiveTab(value) {
    isActiveTab.value = value
  }

  // ========== 语言设置 ==========
  /**
   * 当前应用语言
   * Store 是语言状态的单一数据源
   * 默认使用浏览器语言，会通过 pinia-plugin-persistedstate 自动持久化
   */
  const locale = ref(getBrowserLocale())

  /**
   * 支持的语言列表
   */
  const supportedLocales = SUPPORT_LOCALES

  /**
   * 设置应用语言
   * @param {string} newLocale - 语言代码 (zh-CN, en-US, pt-BR)
   */
  function setLocale(newLocale) {
    if (!SUPPORT_LOCALES.includes(newLocale)) {
      console.warn(`[GlobalStore] Unsupported locale: ${newLocale}`)
      return
    }

    // 更新 store 状态（会自动持久化）
    locale.value = newLocale
  }

  // ========== 应用初始化状态 ==========
  /**
   * 应用是否正在初始化（刷新 token、恢复用户状态等）
   * 用于避免页面初始化时的状态闪烁
   */
  const userStateInitializing = ref(true)

  /**
   * 设置应用初始化状态
   * @param {boolean} value - true 表示正在初始化，false 表示初始化完成
   */
  function setUserStateInitializing(value) {
    userStateInitializing.value = value
  }

  // ========== 设备信息 ==========
  const deviceInfo = getDeviceInfo()

  // ========== 应用静态配置 ==========
  /**
   * 应用静态配置
   */
  const staticConfig = ref(null)

  /**
   * 获取应用静态配置
   */
  async function fetchStaticConfig() {
    try {
      const config = await getStaticConfig({ hideLoading: true })
      staticConfig.value = config
      return config
    } catch (error) {
      console.error('[GlobalStore] Failed to fetch static config:', error)
      return null
    }
  }

  // ========== Match Pop 管理 ==========
  /**
   * 上次调用 matchPopReport 的时间戳（毫秒）
   * 用于控制调用频率（最小间隔 2 分钟）
   */
  const lastMatchPopTime = ref(0)

  /**
   * 设置上次 matchPop 调用时间
   * @param {number} timestamp - 时间戳（毫秒）
   */
  function setLastMatchPopTime(timestamp) {
    lastMatchPopTime.value = timestamp
  }

  // ========== 页面可见性状态 ==========
  /**
   * 页面可见性状态（'visible' | 'hidden'）
   * 使用 VueUse 的 useDocumentVisibility 监听
   * 持久化以便跨会话追踪
   */
  const documentVisibility = useDocumentVisibility()
  const isDocumentVisible = computed(() => documentVisibility.value === 'visible')

  return {
    // 标签页状态
    isActiveTab,
    setActiveTab,

    // 应用初始化状态
    userStateInitializing,
    setUserStateInitializing,

    // 语言设置
    locale,
    supportedLocales,
    setLocale,

    // 设备信息
    deviceInfo,

    // 应用静态配置
    staticConfig,
    fetchStaticConfig,

    // 页面可见性
    documentVisibility,
    isDocumentVisible,

    // Match Pop 管理
    lastMatchPopTime,
    setLastMatchPopTime,
  }
}, {
  // 持久化配置
  persist: {
    key: 'GLOBAL_CONFIG',
    storage: localStorage,
    pick: ['lastMatchPopTime'], // 持久化 matchPop 时间
  }
})
